import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, UntypedFormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import { DatePickerComponent } from '@cintra-payroll-hr/components';
import { PasswordResetControlsComponent } from '../password-reset-controls/password-reset-controls.component';
import { OnscreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { authenticationRoutes } from '../authentication-routes';
import { ResetPasswordService } from './reset-password.service';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { LoggingService } from '@logging/logging.service';
import { getResetPassword400Modal } from './reset-password-form.errors';
import { H4Component } from '@design/typography/h4/h4.component';
import { InputComponent } from '@design/forms/input/input.component';
import { ButtonComponent } from '@design/buttons/button/button.component';
import { TestIdDirective } from '@design/core/test-id.directive';
import { AutoFocusDirective } from '@design/forms/app-auto-focus.directive';

@Component({
  selector: 'app-reset-password-form',
  templateUrl: './reset-password-form.component.html',
  styleUrls: ['./reset-password-form.component.scss'],
  providers: [ResetPasswordService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    H4Component,
    InputComponent,
    ButtonComponent,
    PasswordResetControlsComponent,
    TestIdDirective,
    AutoFocusDirective,
    DatePickerComponent
  ]
})
export class ResetPasswordFormComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messagingService = inject(OnscreenMessagingService);
  private readonly resetPasswordService = inject(ResetPasswordService);
  private readonly authenticationState = inject(ActiveAuthenticationStateService);
  private readonly logger = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  inFlight$ = this.authenticationState.inFlight$;

  emailControl = new UntypedFormControl('', [Validators.required, Validators.email]);
  resetPasswordRequestFormGroup: UntypedFormGroup = new UntypedFormGroup({
    email: this.emailControl
  });

  token = this.route.snapshot.queryParams['t'];
  currentStep: 'RequestToken' | 'CompleteResetPassword' = !this.token ? 'RequestToken' : 'CompleteResetPassword';

  /**
   * Set from GET /auth/reset-password/validate.
   * When true, the DOB field is shown and required (email-change activation flow).
   */
  requiresDateOfBirth = signal(false);

  /**
   * False until GET /validate finishes (success or fail-open error).
   * Prevents submitting before we know whether DOB is required.
   */
  tokenValidationSettled = signal(this.currentStep !== 'CompleteResetPassword');

  resetPasswordFormGroup: UntypedFormGroup = new UntypedFormGroup({
    dateOfBirth: new UntypedFormControl(null)
  });

  @ViewChild('passwordResetControls') passwordResetControls: PasswordResetControlsComponent;

  constructor() {
    this.logger.trace('ResetPasswordFormComponent - token: ', this.token);
  }

  ngOnInit(): void {
    if (this.currentStep !== 'CompleteResetPassword' || !this.token) {
      return;
    }

    this.resetPasswordService
      .validateResetPasswordToken$(this.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ requiresDateOfBirth }) => {
          this.applyDateOfBirthRequirement(requiresDateOfBirth);
          this.tokenValidationSettled.set(true);
        },
        // Fail open: allow password reset without DOB if validate fails; POST may return
        // DateOfBirthRequired and reveal the field via applyDateOfBirthRequirement(true).
        error: (err) => {
          this.logger.error('ResetPasswordFormComponent: token validation failed', err);
          this.tokenValidationSettled.set(true);
        }
      });
  }

  requestResetPassword(): void {
    if (!this.resetPasswordRequestFormGroup.valid) {
      return;
    }

    const email = this.emailControl.value.toLowerCase();
    this.resetPasswordService
      .requestResetPassword$(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          void this.router.navigateByUrl(authenticationRoutes.signIn, {
            state: {
              message: {
                title: 'Please check your email for instructions on how to regain access to your account.'
              }
            }
          });
        },
        error: () => this.messagingService.warn('An unexpected error occurred.')
      });
  }

  completeResetPassword(): void {
    if (!this.tokenValidationSettled()) {
      return;
    }

    if (!this.resetPasswordFormGroup.valid) {
      this.resetPasswordFormGroup.markAllAsTouched();
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Only include DOB when the API flagged this token as requiring it.
    const dateOfBirth = this.requiresDateOfBirth() ? this.resetPasswordFormGroup.get('dateOfBirth')?.value : undefined;

    this.resetPasswordService
      .resetPassword$(this.token, this.passwordResetControls.password, dateOfBirth)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          // Stay on the reset form when we show a message so the user can retry.
          if (error.status === 400) {
            const errorBody = error?.error;
            if (errorBody?.status === 'DateOfBirthRequired') {
              this.applyDateOfBirthRequirement(true);
            }
            const { title, message } = getResetPassword400Modal(errorBody);
            this.messagingService.openAlertDialog({ title, message });
            return EMPTY;
          }

          if (error.status === 500) {
            this.messagingService.warn(
              'There was an error processing the request.\n\nPlease try again later or contact your administrator.'
            );
            return EMPTY;
          }

          return throwError(() => error);
        })
      )
      .subscribe(() => {
        void this.router.navigateByUrl(authenticationRoutes.signIn);
      });
  }

  cancel(): void {
    void this.router.navigate(['/auth', { outlets: { top: ['sign-in'] } }]);
  }

  private applyDateOfBirthRequirement(requiresDateOfBirth: boolean): void {
    this.requiresDateOfBirth.set(requiresDateOfBirth);

    const dateOfBirthControl = this.resetPasswordFormGroup.get('dateOfBirth');
    if (!dateOfBirthControl) {
      return;
    }

    if (requiresDateOfBirth) {
      dateOfBirthControl.setValidators([Validators.required]);
    } else {
      dateOfBirthControl.clearValidators();
    }
    dateOfBirthControl.updateValueAndValidity();
    this.changeDetectorRef.markForCheck();
  }
}
